import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { IoClose } from 'react-icons/io5'
import {
  withdrawConsent,
  closeWithdrawModal
} from '../../redux/reducers/consentReducer'
import {
  MINI_APP_IDS,
  APP_ID_TO_SCOPE,
  CONSENT_CONFIG,
  SUPER_APP_CONFIG,
  isScopeMatch
} from '../../utils/constants/config'
import { ROUTES } from '../../utils/constants'
import { useRevokeConsent, useAvailableConsents } from '../../hooks/useConsent'
import { useToast } from '../UI/ToastProvider'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
const WithdrawConsentModal = ({
  isOpen: propIsOpen,
  miniAppId: propMiniAppId,
  onClose: propOnClose,
  onConfirm: propOnConfirm
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { showError } = useToast()
  const reduxWithdraw = useSelector((state) => state.consent?.withdrawModal)
  const trackEvent = useTrackEvent()

  const currentPath = (location?.pathname || '').toLowerCase().replace(/\/+$/, '')
  const privacyPath = (ROUTES?.privacy || '/privacy').toLowerCase().replace(/\/+$/, '')
  const termsPath = (ROUTES?.terms || '/terms').toLowerCase().replace(/\/+$/, '')

  const isLegalPage =
    currentPath === privacyPath ||
    currentPath === termsPath ||
    currentPath.startsWith(`${privacyPath}/`) ||
    currentPath.startsWith(`${termsPath}/`)

  const isOpen =
    propIsOpen !== undefined ? propIsOpen : (reduxWithdraw?.isOpen || false)
  const miniAppId =
    propMiniAppId || reduxWithdraw?.miniAppId || MINI_APP_IDS.CREATE

  const appConsent = useSelector((state) => state.consent?.miniApps?.[miniAppId])
  const { data: availableData } = useAvailableConsents()
  const { mutateAsync: revokeConsent } = useRevokeConsent()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const appResult = availableData?.results?.find((item) =>
    isScopeMatch(item.scope, miniAppId)
  )
  const appName = appResult?.scope || APP_ID_TO_SCOPE[miniAppId] || 'App'
  const withdrawTitle = `Withdraw ${appName} consent?`
  const withdrawMessage = `${appName} features will be unavailable until you agree again. Your account and other apps are not affected.`

  if (!isOpen || isLegalPage) return null

  const handleClose = () => {
    if (isSubmitting) return
    if (propOnClose) {
      propOnClose()
    } else {
      dispatch(closeWithdrawModal())
    }
  }

  const handleWithdraw = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      const isAcceptedAction = (action) =>
        action?.toUpperCase() === 'ACCEPTED' || action?.toUpperCase() === 'REACCEPTED'

      // Check availableData history_data across all results matching scope
      const allAppHistory = (availableData?.results || [])
        .filter((item) => isScopeMatch(item.scope, miniAppId))
        .flatMap((item) => item.history_data || [])

      let mandatoryId = null
      let optionalId = null
      let isOptionalAccepted = false

      if (allAppHistory.length > 0) {
        const sorted = [...allAppHistory].sort((a, b) => {
          const timeA = new Date(b.updated_at || b.created_at || b.timestamp || b.revoked_at || 0).getTime()
          const timeB = new Date(a.updated_at || a.created_at || a.timestamp || a.revoked_at || 0).getTime()
          return timeA - timeB
        })

        const latestMandatory = sorted.find(
          (item) => item.consent_type?.toUpperCase() === 'MANDATORY'
        )
        if (latestMandatory && isAcceptedAction(latestMandatory.action)) {
          mandatoryId = latestMandatory.consent_id || latestMandatory.id
        }

        const latestOptional = sorted.find(
          (item) =>
            item.consent_type?.toUpperCase() === 'OPTIONAL' ||
            item.consent_type?.toUpperCase() === 'PERSONALISATION'
        )
        if (latestOptional && isAcceptedAction(latestOptional.action)) {
          isOptionalAccepted = true
          optionalId = latestOptional.consent_id || latestOptional.id
        }
      }

      // Fallback to appConsent from Redux
      if (!mandatoryId && appConsent?.consentId) {
        mandatoryId = appConsent.consentId
      }

      if (!optionalId && appConsent?.hasOptionalConsent && appConsent?.optionalConsentId) {
        isOptionalAccepted = true
        optionalId = appConsent.optionalConsentId
      }

      // 1. Revoke mandatory consent
      if (mandatoryId) {
        const revokedData = await revokeConsent({ consent_id: mandatoryId })
        const consentRevokedData = revokedData?.data
          trackEvent(GA_EVENTS.consent_revoked, {
            mini_app: miniAppId,
            consent_version: consentRevokedData?.consent_version,
            consent_scope: consentRevokedData?.scope,
            consent_status: consentRevokedData?.status?.toLowerCase() ?? '',
            timestamp: new Date().toISOString()
          })   
      }

      // 2. Only revoke optional consent if it is currently accepted
      if (isOptionalAccepted && optionalId && optionalId !== mandatoryId) {
        try {
          const revokedData = await revokeConsent({ consent_id: optionalId })
          const consentRevokedData = revokedData?.data
          trackEvent(GA_EVENTS.consent_revoked, {
            mini_app: miniAppId,
            consent_version: consentRevokedData?.consent_version,
            consent_scope: consentRevokedData?.scope,
            consent_status: consentRevokedData?.status?.toLowerCase() ?? '',
            timestamp: new Date().toISOString()
          })
        } catch {
          // Silent catch if optional revoke was already processed
        }
      }

      // ONLY dispatch withdraw on success
      dispatch(withdrawConsent(miniAppId))
      if (propOnConfirm) {
        propOnConfirm(miniAppId)
      }
      if (propOnClose) {
        propOnClose()
      } else {
        dispatch(closeWithdrawModal())
      }
    } catch (err) {
      showError(err?.message || CONSENT_CONFIG.FAILED_WITHDRAW)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 animate-fade-in"
      onClick={handleClose}
    >
      <div
        className="bg-[#0F172A] border border-white/10 rounded-2xl md:rounded-[20px] w-full max-w-[340px] sm:max-w-[420px] md:max-w-[460px] shadow-2xl p-5 sm:p-6 flex flex-col text-white text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-[17px] font-bold text-white m-0 flex-1">
            {withdrawTitle}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-[#94A3B8] hover:text-white flex items-center justify-center transition-colors cursor-pointer border-0 shrink-0 ml-3"
          >
            <IoClose size={18} />
          </button>
        </div>

        {/* Message */}
        <p className="text-[13px] leading-relaxed text-[#98A2B3] mt-3.5 mb-5 sm:mb-6">
          {withdrawMessage}
        </p>

        {/* Action Buttons: Stacked vertically on mweb (mobile), side-by-side on desktop */}
        <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 sm:gap-3.5">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full h-11 rounded-full border border-white/10 hover:border-white/20 bg-transparent hover:bg-[#223147] text-[#F2F4F7] text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center disabled:opacity-50"
          >
            {SUPER_APP_CONFIG?.CANCEL || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleWithdraw}
            disabled={isSubmitting}
            className="w-full h-11 rounded-full border border-[#A3262A] hover:border-[#E05252] bg-transparent hover:bg-[#A3262A]/15 text-[#E05252] text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center disabled:opacity-50"
          >
            {isSubmitting
              ? CONSENT_CONFIG.WITHDRAWING
              : CONSENT_CONFIG.WITHDRAW_CONSENT}
          </button>
        </div>
      </div>
    </div>
  )
}

WithdrawConsentModal.propTypes = {
  isOpen: PropTypes.bool,
  miniAppId: PropTypes.string,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func
}

export default WithdrawConsentModal
